import { useState } from 'react'
import { AlertTriangle, CheckCircle2, Lock, XCircle } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { ErrorNote } from '@/components/ui/misc'
import { Dialog } from '@/components/ui/overlay'
import { api, ApiError, type DeleteResponse, type ObjectMeta } from '@/lib/api'

/**
 * Confirms and reports a delete.
 *
 * On WORM storage a partial success is normal rather than exceptional: some
 * objects delete while others are refused by a retention period or hold. The
 * dialog therefore always reports per-object outcomes instead of a single
 * success or failure.
 */
export function DeleteDialog({
  bucket,
  keys,
  metas,
  onClose,
  onDeleted,
}: {
  bucket: string
  keys: string[]
  metas: Map<string, ObjectMeta>
  onClose: () => void
  onDeleted: (deletedKeys: string[]) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<DeleteResponse | null>(null)

  const locked = keys.filter((key) => metas.get(key)?.locked)
  const deletable = keys.filter((key) => !metas.get(key)?.locked)

  async function confirm() {
    setBusy(true)
    setError(null)
    try {
      // Every key is sent, including ones already known to be locked, so the
      // server's answer is authoritative — the cached lock state could be
      // stale, and a retention period may have expired since it was read.
      const res = await api.deleteObjects(bucket, keys)
      setResult(res)
      onDeleted(res.results.filter((r) => r.ok).map((r) => r.key))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed')
    } finally {
      setBusy(false)
    }
  }

  if (result) {
    return (
      <Dialog
        open
        onClose={onClose}
        title="Delete finished"
        footer={
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        }
      >
        <div className="space-y-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 text-success" />
              {result.deleted} deleted
            </span>
            {result.blocked > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Lock className="h-4 w-4 text-danger" />
                {result.blocked} blocked by retention
              </span>
            )}
            {result.failed > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <XCircle className="h-4 w-4 text-danger" />
                {result.failed} failed
              </span>
            )}
          </div>

          {result.results.some((r) => !r.ok) && (
            <ul className="space-y-1.5 rounded-md border border-border bg-surface p-3">
              {result.results
                .filter((r) => !r.ok)
                .map((r) => (
                  <li key={r.key} className="text-sm">
                    <span className="font-mono text-xs">{r.key}</span>
                    <span className="block text-xs text-muted-foreground">{r.message}</span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={keys.length === 1 ? 'Delete this object?' : `Delete ${keys.length} objects?`}
      description="This cannot be undone."
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            loading={busy}
            disabled={deletable.length === 0}
          >
            {deletable.length === keys.length
              ? `Delete ${keys.length}`
              : `Delete ${deletable.length} of ${keys.length}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}

        {locked.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2.5 text-sm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div>
              <p className="font-medium">
                {locked.length} of these {locked.length === 1 ? 'is' : 'are'} protected by retention
                or a hold
              </p>
              <p className="text-muted-foreground">
                HCP will refuse to delete {locked.length === 1 ? 'it' : 'them'}. The rest will still
                be deleted, and you will get a per-object report.
              </p>
            </div>
          </div>
        )}

        {deletable.length === 0 && (
          <ErrorNote>
            Every selected object is protected by retention or a hold, so nothing can be deleted.
          </ErrorNote>
        )}

        <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border border-border bg-surface p-3">
          {keys.map((key) => {
            const isLocked = metas.get(key)?.locked
            return (
              <li key={key} className="flex items-center gap-2 text-sm">
                {isLocked && <Lock className="h-3.5 w-3.5 shrink-0 text-danger" />}
                <span className="truncate font-mono text-xs">{key}</span>
              </li>
            )
          })}
        </ul>
      </div>
    </Dialog>
  )
}
